import { StatusModal } from '@/components/StatusModal';
import { CenyVelikostiModal } from '@/components/druhaJakost/CenyVelikostiModal';
import { NapojitKarta } from '@/components/druhaJakost/NapojitKarta';
import { ZalozeneTabulka } from '@/components/druhaJakost/ZalozeneTabulka';
import { apiFetch } from '@/lib/apiFetch';
import {
  type Existujici,
  type Navrh,
  type Produkt,
  type ShoptetNahled,
  type Zalozena,
  cenaPoSleve,
  errMsg,
  fmtDateTime,
  fmtKc,
  parseSleva,
  popisAkce,
  zalozitShoptet,
} from '@/lib/druhaJakost';
import { useCallback, useEffect, useState } from 'react';

/**
 * 2. jakost — vybereš originální produkt a ve výrobě vznikne jeho kopie
 * s dalším volným kódem v řadě a názvem „🟠 2. JAKOST - …".
 */
export function DruhaJakostPage() {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<Produkt[]>([]);
  const [open, setOpen] = useState(false);
  const [navrh, setNavrh] = useState<Navrh | null>(null);
  const [navrhError, setNavrhError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sleva, setSleva] = useState('');
  const [shoptet, setShoptet] = useState<ShoptetNahled | null>(null);
  const [shoptetError, setShoptetError] = useState('');
  const [shoptetLoading, setShoptetLoading] = useState(false);
  // 2. jakost, která už je ve výrobě, ale v Shoptetu chybí → zakládá se jen Shoptet.
  const [doplnit, setDoplnit] = useState<Existujici | null>(null);
  const [items, setItems] = useState<Zalozena[]>([]);
  const [listError, setListError] = useState('');
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  // Vybraná starší 2. jakost k napojení; po napojení rovnou okno s cenami.
  const [napojit, setNapojit] = useState<string | null>(null);
  const [cenyItem, setCenyItem] = useState<Zalozena | null>(null);

  const loadList = useCallback(async (): Promise<Zalozena[]> => {
    setListError('');
    try {
      const list = (await apiFetch<{ items: Zalozena[] }>('/api/druha-jakost')).items;
      setItems(list);
      return list;
    } catch (e: unknown) {
      setListError(errMsg(e, 'Nepodařilo se načíst založené 2. jakosti.'));
      return [];
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // Našeptávač originálů — filtruje backend, 2. jakosti nenabízí.
  useEffect(() => {
    if (query.trim().length < 2) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      apiFetch<{ produkty: Produkt[] }>(
        `/api/druha-jakost/produkty?q=${encodeURIComponent(query.trim())}`,
      )
        .then((d) => {
          if (!cancelled) setHits(d.produkty);
        })
        .catch(() => {
          if (!cancelled) setHits([]);
        });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  /** Náhled ze Shoptetu — pro novou 2. jakost, nebo pro už založenou (`cil`). */
  const nactiShoptet = (code: string, cil: Existujici | null) => {
    setShoptet(null);
    setShoptetError('');
    setShoptetLoading(true);
    const q = `code=${encodeURIComponent(code)}${cil ? `&druha_id=${cil.id}` : ''}`;
    apiFetch<ShoptetNahled>(`/api/druha-jakost/shoptet-cz/nahled?${q}`)
      .then(setShoptet)
      .catch((e: unknown) => setShoptetError(errMsg(e, 'Náhled ze Shoptetu selhal.')))
      .finally(() => setShoptetLoading(false));
  };

  const prepnout = (cil: Existujici | null) => {
    if (!navrh) return;
    setDoplnit(cil);
    nactiShoptet(navrh.original.code, cil);
  };

  const vybrat = async (p: Produkt) => {
    setOpen(false);
    setQuery(`${p.code} ${p.name}`);
    setNavrh(null);
    setNavrhError('');
    setShoptet(null);
    setShoptetError('');
    setDoplnit(null);
    setSleva('');
    setNapojit(null);
    if (p.legacy) {
      setNapojit(p.code);
      return;
    }
    setLoading(true);
    // Vybraná 2. jakost (hledání podle jejího kódu) → pracuje se s originálem.
    const origCode = p.original_code ?? p.code;
    try {
      const n = await apiFetch<Navrh>(
        `/api/druha-jakost/navrh?code=${encodeURIComponent(origCode)}`,
      );
      if (p.original_code) {
        const tahle = n.existing.find((e) => e.new_code === p.code);
        if (tahle?.v_shoptetu) {
          // Už je v Shoptetu → rovnou ceny: originál, aktuální cena a sleva, změna.
          const { items: jedna } = await apiFetch<{ items: Zalozena[] }>(
            `/api/druha-jakost?code=${encodeURIComponent(p.code)}`,
          );
          if (jedna[0]) setCenyItem(jedna[0]);
          else setNavrhError(`${p.code} se nepodařilo načíst.`);
          return;
        }
        setNavrh(n);
        setDoplnit(tahle ?? null);
        nactiShoptet(origCode, tahle ?? null);
        return;
      }
      setNavrh(n);
      // Je už 2. jakost ve výrobě, ale ne v Shoptetu? Pak nabídneme jen Shoptet.
      const chybi = n.existing.find((e) => !e.v_shoptetu) ?? null;
      setDoplnit(chybi);
      nactiShoptet(origCode, chybi);
    } catch (e: unknown) {
      setNavrhError(errMsg(e, 'Nepodařilo se připravit návrh.'));
    } finally {
      setLoading(false);
    }
  };

  const slevaPct = parseSleva(sleva);
  // Do Shoptetu jen s platným náhledem a cenou; když náhled selže, jde
  // založit aspoň výroba a Shoptet se doplní ze seznamu níž.
  const sShoptetem = !!shoptet && !shoptetError;
  const lzeZalozit = doplnit
    ? !shoptetLoading && sShoptetem && !!slevaPct
    : !shoptetLoading && (sShoptetem ? !!slevaPct : !!shoptetError);

  const zavrit = () => {
    setNavrh(null);
    setShoptet(null);
    setDoplnit(null);
    setSleva('');
    setQuery('');
    void loadList();
  };

  const zalozit = async () => {
    if (!navrh || !lzeZalozit) return;
    setSaving(true);
    if (doplnit && slevaPct) {
      try {
        const warning = await zalozitShoptet(doplnit.id, slevaPct);
        setToast({
          msg: warning
            ? `${doplnit.new_code} založeno, ale: ${warning}`
            : `${doplnit.new_code} založeno v Shoptetu`,
          kind: 'success',
        });
        zavrit();
      } catch (e: unknown) {
        setToast({ msg: errMsg(e, 'Založení v Shoptetu selhalo.'), kind: 'error' });
      } finally {
        setSaving(false);
      }
      return;
    }
    try {
      const res = await apiFetch<{ druha_id: number; code: string }>('/api/druha-jakost', {
        method: 'POST',
        body: { code: navrh.original.code },
      });
      let msg = `Založeno ve výrobě: ${res.code}`;
      let ok = true;
      if (sShoptetem && slevaPct) {
        try {
          const warning = await zalozitShoptet(res.druha_id, slevaPct);
          msg = warning
            ? `${res.code} založeno, ale: ${warning}`
            : `${res.code} založeno ve výrobě i v Shoptetu`;
        } catch (e: unknown) {
          ok = false;
          msg = `${res.code} je ve výrobě, ale Shoptet selhal: ${errMsg(e, 'neznámá chyba')}`;
        }
      }
      setToast({ msg, kind: ok ? 'success' : 'error' });
      zavrit();
    } catch (e: unknown) {
      setToast({ msg: errMsg(e, 'Založení selhalo.'), kind: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">2. jakost</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Vyber originální produkt. Ve výrobě vznikne jeho kopie s dalším volným kódem v řadě a
          názvem „🟠 2. JAKOST - …" a stejná kopie se založí v Shoptetu CZ — všechny velikosti se
          skladem 0, dostupnost Skladem / Vyprodáno, jen v kategorii 2. jakost, hned viditelná.
        </p>
      </div>

      {/* Vyhledávání originálu (nebo už založené 2. jakosti podle jejího kódu) */}
      <div className="relative max-w-xl mb-6">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Hledat produkt podle kódu nebo názvu…"
          className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-base text-text focus:outline-none focus:border-primary"
        />
        {open && hits.length > 0 && (
          <ul className="absolute z-20 mt-1 w-full max-h-80 overflow-y-auto rounded-lg border border-border bg-surface shadow-modal">
            {hits.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => void vybrat(p)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-surface-hover cursor-pointer transition-colors"
                >
                  {p.image ? (
                    <img
                      src={p.image}
                      alt=""
                      loading="lazy"
                      className="w-10 h-10 rounded object-contain border border-border bg-bg shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded border border-border bg-bg shrink-0" />
                  )}
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-text truncate">{p.name}</span>
                    <span className="block text-xs text-text-muted font-mono">
                      {p.code}
                      {p.original_code && ` · 2. jakost od ${p.original_code}`}
                      {p.legacy && ' · starší 2. jakost'}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {loading && <p className="text-sm text-text-muted mb-6">Připravuji návrh…</p>}
      {navrhError && !loading && (
        <div className="rounded-lg border border-danger/20 bg-danger-light p-4 mb-6 text-sm text-danger max-w-2xl">
          {navrhError}
        </div>
      )}

      {napojit && (
        <NapojitKarta
          code={napojit}
          onZrusit={() => {
            setNapojit(null);
            setQuery('');
          }}
          onNapojeno={(id) => {
            setNapojit(null);
            setQuery('');
            void loadList().then((list) => setCenyItem(list.find((i) => i.id === id) ?? null));
          }}
        />
      )}

      {navrh && !loading && (
        <div className="bg-surface rounded-xl shadow-card p-5 mb-8 max-w-2xl">
          {doplnit && (
            <div className="mb-4 rounded-lg border border-primary/30 bg-primary-100 p-3 text-sm text-text">
              <b className="font-mono">{doplnit.new_code}</b> už je ve výrobě, ale v Shoptetu CZ
              chybí — založí se <b>jen v Shoptetu</b>.{' '}
              <button
                type="button"
                onClick={() => prepnout(null)}
                className="underline text-text-secondary cursor-pointer"
              >
                Místo toho založit novou 2. jakost
              </button>
            </div>
          )}
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-[15px]">
            <div className="text-text-muted">Originál</div>
            <div className="text-text">
              <span className="font-mono">{navrh.original.code}</span> {navrh.original.name}
            </div>
            <div className="text-text-muted">{doplnit ? 'Kód 2. jakosti' : 'Nový kód'}</div>
            <div className="font-mono font-bold text-text">
              {doplnit ? doplnit.new_code : navrh.new_code}
            </div>
            {!doplnit && (
              <>
                <div className="text-text-muted">Nový název</div>
                <div className="font-semibold text-text">{navrh.new_name}</div>
              </>
            )}
            <div className="text-text-muted">Shoptet CZ</div>
            <div className="text-text">
              {shoptetLoading && <span className="text-text-muted">Načítám ze Shoptetu…</span>}
              {shoptetError && <span className="text-danger">{shoptetError}</span>}
              {shoptet && (
                <>
                  Kategorie <b>{shoptet.category}</b> · příznak <b>{shoptet.flag}</b> ·{' '}
                  {shoptet.variants.length} velikostí · {shoptet.images} fotek
                </>
              )}
            </div>
            {shoptet && (
              <>
                <label className="text-text-muted self-center" htmlFor="sleva-2j">
                  Sleva
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="sleva-2j"
                    type="text"
                    inputMode="decimal"
                    value={sleva}
                    onChange={(e) => setSleva(e.target.value)}
                    placeholder="např. 30"
                    className="w-24 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text text-right focus:outline-none focus:border-primary"
                  />
                  <span className="text-text-secondary">
                    % z akční ceny originálu, cena zaokrouhlená dolů na celé Kč
                  </span>
                </div>
                <div className="text-text-muted">Ceny</div>
                <div>
                  <table className="text-sm">
                    <thead>
                      <tr className="text-xs text-text-muted">
                        <th className="pr-4 text-left font-normal">Kód</th>
                        <th className="pr-4 text-right font-normal">Cena</th>
                        <th className="pr-4 text-right font-normal">Akční originálu</th>
                        <th className="text-right font-normal">Akční 2. jakost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shoptet.variants.map((v) => (
                        <tr key={v.to}>
                          <td className="pr-4 font-mono text-text-secondary">{v.to}</td>
                          <td className="pr-4 text-right tabular-nums text-text-muted">
                            {v.orig_price !== null ? fmtKc(v.orig_price) : 'bez ceny'}
                          </td>
                          <td className="pr-4 text-right tabular-nums text-text-muted">
                            {popisAkce(v)}
                          </td>
                          <td className="text-right tabular-nums font-semibold text-text">
                            {v.base_price !== null && slevaPct
                              ? fmtKc(cenaPoSleve(v.base_price, slevaPct))
                              : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-text-muted mt-1">
                    Cena, standardní a nákupní cena jako originál. Akční cena = akční cena originálu
                    − sleva (bez akce z běžné ceny), Akce od/do jako originál.
                  </p>
                </div>
              </>
            )}
          </div>
          {shoptetError && !doplnit && (
            <p className="mt-3 text-sm text-text-secondary">
              Založí se jen ve výrobě, Shoptet půjde doplnit ze seznamu níž.
            </p>
          )}
          {!doplnit && navrh.existing.length > 0 && (
            <div className="mt-4 rounded-lg border border-warning/40 bg-warning-light p-3 text-sm text-text">
              Tenhle produkt už má 2. jakost:{' '}
              <span className="font-mono">{navrh.existing.map((e) => e.new_code).join(', ')}</span>.
              Opravdu chceš založit další?{' '}
              {navrh.existing
                .filter((e) => !e.v_shoptetu)
                .map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => prepnout(e)}
                    className="underline cursor-pointer mr-2"
                  >
                    Doplnit Shoptet k {e.new_code}
                  </button>
                ))}
            </div>
          )}
          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={() => void zalozit()}
              disabled={saving || !lzeZalozit}
              className="rounded-lg bg-action px-5 py-2.5 text-sm font-semibold text-white hover:brightness-95 disabled:opacity-50 cursor-pointer"
            >
              {saving ? 'Zakládám…' : doplnit ? 'Založit jen v Shoptetu' : 'Založit 2. jakost'}
            </button>
            <button
              type="button"
              onClick={() => {
                setNavrh(null);
                setDoplnit(null);
                setQuery('');
              }}
              className="rounded-lg border border-border px-5 py-2.5 text-sm text-text-secondary hover:bg-surface-hover cursor-pointer"
            >
              Zrušit
            </button>
          </div>
        </div>
      )}

      <ZalozeneTabulka
        items={items}
        listError={listError}
        onDone={(msg, ok) => {
          setToast({ msg, kind: ok ? 'success' : 'error' });
          void loadList();
        }}
      />

      {cenyItem && (
        <CenyVelikostiModal
          item={cenyItem}
          onClose={() => setCenyItem(null)}
          onDone={(msg, ok) => {
            setToast({ msg, kind: ok ? 'success' : 'error' });
            void loadList();
          }}
        />
      )}

      {toast && (
        <StatusModal kind={toast.kind} message={toast.msg} onClose={() => setToast(null)} />
      )}
    </>
  );
}
